import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { EmployeeChatView, LinkMessage, LINK_MESSAGES } from './EmployeeSessionPage'

type Blocked = { title: string; message: string }

const BLOCKED: Record<'used' | 'expired' | 'revoked' | 'invalid', Blocked> = {
  used:    { title: 'Link already used', message: LINK_MESSAGES.used },
  expired: { title: 'Link expired',      message: LINK_MESSAGES.expired },
  revoked: { title: 'Link cancelled',    message: LINK_MESSAGES.revoked },
  invalid: { title: 'Link not valid',    message: LINK_MESSAGES.invalid },
}

/**
 * Magic-link landing: /join?token=…
 *
 * The link is checked first (read-only — email scanners open links too and must
 * not burn the token). A used, expired or cancelled link shows why straight
 * away; a valid one opens the chat, and the token is redeemed only when the
 * employee accepts the call. One link = one session, enforced by the server.
 */
export default function JoinPage() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const [name,    setName]    = useState<string | null>(null)
  const [blocked, setBlocked] = useState<Blocked | null>(token ? null : BLOCKED.invalid)
  const redeemed = useRef(false)

  useEffect(() => {
    if (!token) return
    let live = true
    fetch(`/api/employee-access/preview?token=${encodeURIComponent(token)}`, { credentials: 'include' })
      .then(async res => {
        const body = await res.json().catch(() => ({})) as { employeeName?: string; state?: keyof typeof BLOCKED | 'valid' }
        if (!live) return
        if (res.ok && body.state === 'valid' && body.employeeName) setName(body.employeeName)
        else setBlocked(BLOCKED[res.ok && body.state && body.state !== 'valid' ? body.state : 'invalid'])
      })
      .catch(() => { if (live) setBlocked(BLOCKED.invalid) })
    return () => { live = false }
  }, [token])

  // Redeem exactly once, when the employee accepts the call.
  const redeemOnStart = useCallback(async () => {
    if (redeemed.current) return
    const res = await fetch('/api/employee-access/redeem', {
      method:      'POST',
      headers:     { 'Content-Type': 'application/json' },
      credentials: 'include',
      body:        JSON.stringify({ token }),
    })
    if (!res.ok) {
      setBlocked(BLOCKED.used)   // used up meanwhile (e.g. opened on another device)
      throw new Error(LINK_MESSAGES.used)
    }
    redeemed.current = true
    window.history.replaceState(null, '', '/session')
  }, [token])

  if (blocked) return <LinkMessage {...blocked} />
  if (!name) return null
  return <EmployeeChatView employeeName={name} beforeStart={redeemOnStart} />
}
